import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  alpha,
  CircularProgress,
  Grid,
  Paper,
  Chip,
  Stack
} from '@mui/material';
import { AccountBalance as FlowIcon } from '@mui/icons-material';
import { ResponsiveSankey } from '@nivo/sankey';
import { Transaction } from '../../types';
import { processSankeyData, formatCurrency, SankeyData } from '../../utils/chartUtils';

interface SankeyFlowChartProps {
  transactions: Transaction[];
  loading?: boolean;
}

const SankeyFlowChart: React.FC<SankeyFlowChartProps> = ({
  transactions,
  loading = false
}) => {
  const theme = useTheme();

  const sankeyData = useMemo(() => {
    return processSankeyData(transactions);
  }, [transactions]);

  // Calculate summary statistics
  const flowStats = useMemo(() => {
    const incomeNodes = sankeyData.nodes.filter(node => node.id.startsWith('income-'));
    const expenseNodes = sankeyData.nodes.filter(node => node.id.startsWith('expense-'));
    const savingsNode = sankeyData.nodes.find(node => node.id === 'savings');
    
    const totalIncome = sankeyData.links
      .filter(link => link.target === 'available-money')
      .reduce((sum, link) => sum + link.value, 0);
    
    const totalExpenses = sankeyData.links
      .filter(link => link.source === 'available-money' && link.target !== 'savings')
      .reduce((sum, link) => sum + link.value, 0);
    
    const savings = sankeyData.links
      .find(link => link.target === 'savings')?.value || 0;

    return {
      incomeSourcesCount: incomeNodes.length,
      expenseCategoriesCount: expenseNodes.length,
      totalIncome,
      totalExpenses,
      savings,
      savingsRate: totalIncome > 0 ? (savings / totalIncome) * 100 : 0
    };
  }, [sankeyData]);

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" height="400px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (sankeyData.nodes.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="600">
            Money Flow Analysis
          </Typography>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="400px"
            color="text.secondary"
          >
            <FlowIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
            <Typography variant="body1" color="text.secondary">
              No transaction data available for flow analysis
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.4)})`,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="600">
          Money Flow Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Visualize how money flows from income sources through to expenses and savings
        </Typography>

        {/* Flow Statistics */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                textAlign: 'center',
                background: alpha(theme.palette.success.main, 0.05),
                border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                borderRadius: 2
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Income Sources
              </Typography>
              <Typography variant="h6" fontWeight="600" color="success.main">
                {flowStats.incomeSourcesCount}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                textAlign: 'center',
                background: alpha(theme.palette.error.main, 0.05),
                border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                borderRadius: 2
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Expense Categories
              </Typography>
              <Typography variant="h6" fontWeight="600" color="error.main">
                {flowStats.expenseCategoriesCount}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                textAlign: 'center',
                background: alpha(theme.palette.info.main, 0.05),
                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                borderRadius: 2
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Savings Rate
              </Typography>
              <Typography variant="h6" fontWeight="600" color="info.main">
                {flowStats.savingsRate.toFixed(1)}%
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                textAlign: 'center',
                background: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                borderRadius: 2
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Net Flow
              </Typography>
              <Typography variant="h6" fontWeight="600" color="primary.main">
                {formatCurrency(flowStats.totalIncome)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Sankey Diagram */}
        <Box sx={{ height: '500px', position: 'relative' }}>
          <ResponsiveSankey
            data={sankeyData}
            margin={{ top: 40, right: 160, bottom: 40, left: 50 }}
            align="justify"
            colors={{ scheme: 'category10' }}
            nodeOpacity={1}
            nodeHoverOthersOpacity={0.35}
            nodeThickness={18}
            nodeSpacing={24}
            nodeBorderWidth={0}
            nodeBorderColor={{
              from: 'color',
              modifiers: [['darker', 0.8]]
            }}
            nodeBorderRadius={3}
            linkOpacity={0.5}
            linkHoverOthersOpacity={0.1}
            linkContract={3}
            enableLinkGradient={true}
            labelPosition="outside"
            labelOrientation="vertical"
            labelPadding={16}
            labelTextColor={{
              from: 'color',
              modifiers: [['darker', 1]]
            }}
            legends={[
              {
                anchor: 'bottom-right',
                direction: 'column',
                translateX: 130,
                itemWidth: 100,
                itemHeight: 14,
                itemDirection: 'right-to-left',
                itemsSpacing: 2,
                itemTextColor: theme.palette.text.primary,
                symbolSize: 14,
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemTextColor: theme.palette.text.secondary
                    }
                  }
                ]
              }
            ]}
            theme={{
              background: 'transparent',
              text: {
                fontSize: 11,
                fill: theme.palette.text.primary,
                outlineWidth: 0,
                outlineColor: 'transparent'
              },
              tooltip: {
                container: {
                  background: alpha(theme.palette.background.paper, 0.95),
                  color: theme.palette.text.primary,
                  fontSize: 12,
                  borderRadius: theme.shape.borderRadius,
                  boxShadow: theme.shadows[4],
                  border: `1px solid ${theme.palette.divider}`
                }
              }
            }}

          />
        </Box>

        {/* Flow Insights */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Flow Insights
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`${flowStats.incomeSourcesCount} Income Sources`}
              size="small"
              variant="outlined"
              sx={{
                backgroundColor: alpha(theme.palette.success.main, 0.1),
                borderColor: alpha(theme.palette.success.main, 0.3),
                color: theme.palette.success.main
              }}
            />
            <Chip
              label={`${flowStats.expenseCategoriesCount} Expense Categories`}
              size="small"
              variant="outlined"
              sx={{
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                borderColor: alpha(theme.palette.error.main, 0.3),
                color: theme.palette.error.main
              }}
            />
            {flowStats.savings > 0 && (
              <Chip
                label={`${flowStats.savingsRate.toFixed(1)}% Saved`}
                size="small"
                variant="outlined"
                sx={{
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  borderColor: alpha(theme.palette.info.main, 0.3),
                  color: theme.palette.info.main
                }}
              />
            )}
            <Chip
              label={`${formatCurrency(flowStats.totalIncome)} Total Flow`}
              size="small"
              variant="outlined"
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: theme.palette.primary.main
              }}
            />
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SankeyFlowChart; 